<template>
	<div>
		<div
			class="node-outer z-50 bg-opacity-100 transition-all ease-in-out"
			:class="[
				{ [`bg-${spec?.style?.bg}`]: !Array.isArray(spec?.style?.bg) },
				spec?.style?.text,
				{ [`rounded-sm`]: nodeStatus === 'completed' },
				{ [`pulse rounded-[10px] border-4 border-primary opacity-100`]: nodeStatus === 'active' },
				{ [`pulseRed rounded-[10px] border-4 border-red opacity-100`]: nodeStatus === 'final' }
			]"
			:style="`min-height: ${nodeHeight}!important;${Array.isArray(spec?.style?.bg) ? `background-color: rgba(${spec?.style?.bg.join(',')}, var(--tw-bg-opacity));` : ''}`"
		>
			<div class="node-tab max-w-80">
				<div class="node-tab-inner justify-between">
					<div class="flex items-center">
						<Sheet :open="open">
							<SheetTrigger as-child>
								<button
									v-if="spec?.type !== 'entrypoint'"
									class="node-tab-edit cursor-default"
									title="Edit"
									@click="editNode($event)"
									@touchstart="editNode($event)"
								>
									<span class="fa-light fa-sliders"></span>
								</button>
							</SheetTrigger>
							<SheetContent
								v-if="open"
								:vars-menu="varsMenu"
								:disable-vars="configuringNode?.disableVars"
								:readonly="readonly"
								@close="close"
							>
								<NodeConfig
									:selected-tenant-id="selectedFlowTenant"
									:selected-account-id="selectedFlowAccount"
									:node="configuringNode"
									:node-data="configuringNodeData"
									:all-nodes="getAllNodes"
									:close-handler="registerSettingsCloseHandler"
									:readonly="readonly"
									:flows="flows"
									@close="close"
									@save="saveNodeData"
								/>
							</SheetContent>
						</Sheet>
					</div>

					<div v-if="spec?.type === 'entrypoint'" class="w-6"></div>

					<div
						class="flex items-center gap-2"
						:class="{
							'-ml-4': (readonly && outcomeData?.data && spec.type !== 'entrypoint') || !readonly
						}"
					>
						<div v-if="spec.style.icon" class="pr-1 opacity-50">
							<span class="fa-light fa-fw" :class="spec.style.icon"></span>
						</div>
						<span
							class="node-tab-inner-title line-clamp-1"
							:class="[
								spec?.style?.text,
								{ 'w-full text-center': spec?.type === 'entrypoint' || readonly }
							]"
							>{{ obj.name }}</span
						>
					</div>

					<div
						v-if="
							spec?.type === 'entrypoint' ||
							(readonly && spec?.type !== 'entrypoint' && !outcomeData?.data)
						"
						class="w-6"
					></div>

					<div
						v-if="!readonly && spec?.type !== 'entrypoint'"
						class="node-tab-delete"
						title="Delete"
						@click="deleteNode"
						@touchstart.prevent="deleteNode"
					>
						<span class="fa-light fa-close"></span>
					</div>
					<Dialog v-if="readonly && outcomeData?.data">
						<DialogTrigger class="cursor-default">
							<div
								class="m-0.5 flex h-6 w-7 items-center justify-center rounded-md bg-transparent p-0.5 transition-colors duration-300 hover:bg-black/30"
								title="View Output"
								@click="$event.preventDefault()"
								@touchstart.prevent="$event.target.click()"
							>
								<span class="fa-light fa-eye"></span>
							</div>
						</DialogTrigger>
						<DialogContent>
							<div class="rounded-lg bg-white p-4 dark:bg-offmaindark">
								<DialogHeader>
									<DialogTitle
										class="dark:text-offdark mb-4 text-lg font-semibold text-offmain dark:text-offwhite"
									>
										{{ spec?.name }} Output
									</DialogTitle>
									<DialogDescription class="sr-only">
										View the output of the {{ spec?.name }} node
									</DialogDescription>
								</DialogHeader>
								<div v-if="outcomeData?.data">
									<OutcomeDisplay :outcome="outcomeData" @close="close" />
								</div>
								<div v-else>
									<p class="text-center text-gray-500 dark:text-gray-400">No output data available</p>
								</div>
							</div>
						</DialogContent>
					</Dialog>
				</div>
				<div class="w-full overflow-hidden whitespace-nowrap px-4 text-center">
					<p class="truncate text-xs" @mouseover="createTooltip($event, configuringNodeData?.node_id)">
						{{ configuringNodeData?.node_id }}
					</p>
				</div>
			</div>
			<div class="flex h-auto min-h-[45px] w-full flex-row py-2">
				<div
					v-if="spec?.type !== 'entrypoint'"
					class="node-input"
					:data-jtk-port="`in:${obj.id}`"
					data-jtk-target="true"
					data-jtk-scope="teleflow"
					:data-jtk-port-type="'input'"
				></div>
				<div class="absolute right-0 flex flex-col gap-2 pt-1">
					<div v-for="output in enabledOutputs" :key="output.id">
						<div class="relative">
							<div class="mr-5 rounded-md bg-black/20 px-2 py-1.5 text-xs">
								{{ output.name }}
							</div>
							<div
								:data-jtk-port="`out:${obj.id}-${output.id}`"
								:data-jtk-source="readonly ? 'false' : 'true'"
								data-jtk-scope="teleflow"
								data-jtk-port-type="output"
								class="node-output-stacked"
							></div>
						</div>
					</div>
					<div
						v-if="needShowMore"
						class="relative"
						@touchstart.prevent="toggleShowAll"
						@click="toggleShowAll"
					>
						<div class="mr-5 rounded-md px-2 py-1.5 text-xs">
							{{ showAll ? 'Show Less' : 'Show More' }}
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { defineComponent } from 'vue'
import { BaseNodeComponent } from '@jsplumbtoolkit/browser-ui-vue3'
import NodeConfig from '@/components/flow/NodeConfig'
import OutcomeDisplay from './OutcomeDisplay.vue'
import flowsApi from '@/scripts/flows/flows.api'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/nodesheet'
import {
	Dialog,
	DialogTrigger,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription
} from '@/components/ui/dialog'

export default defineComponent({
	components: {
		Sheet,
		SheetContent,
		SheetTrigger,
		NodeConfig,
		OutcomeDisplay,
		Dialog,
		DialogTrigger,
		DialogContent,
		DialogHeader,
		DialogTitle,
		DialogDescription
	},
	mixins: [BaseNodeComponent],
	inject: {
		selectedFlowTenant: {
			default: null
		},
		selectedFlowAccount: {
			default: null
		}
	},
	props: {
		spec: {
			type: Object,
			required: true
		},
		flowNodes: {
			type: Array,
			required: true
		},
		readonly: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			outputs: this.spec?.outputs || [],
			showAll: false,
			needShowMore: this.spec?.outputs?.some((o) => !o.defaultVisible) || false,
			open: false,
			originalData: '',
			settingsCloseHandler: null,
			refreshKey: 0,
			flows: [],
			varsMenu: {}
		}
	},
	computed: {
		enabledOutputs() {
			return this.outputs.filter((o) => this.showAll || o.defaultVisible || o.visible)
		},
		nodeHeight() {
			return `${this.enabledOutputs.length * 36 + 66 + (this.needShowMore ? 36 : 0)}px`
		},
		configuringNode() {
			return this.flowNodes.find((n) => n.type === this.getNode().data.type)
		},
		configuringNodeData() {
			this.refreshKey
			return this.getNode().data.config
		},
		nodeStatus() {
			return this.getNode().data.history?.status
		},
		outcomeData() {
			const outcome = this.getNode().data?.history?.outcome

			if (!outcome) return null

			const exit = this.spec.outputs?.find((o) => o.id === outcome.id)

			return {
				exit: exit ? exit.name : outcome.id?.toProperCase(),
				data: Object.keys(outcome.data || {}).map((k) => {
					const varSpec = this.spec.variables?.find((v) => v.name === k)

					if (varSpec) {
						return {
							name: varSpec.label,
							value: outcome.data[k],
							type: varSpec.type
						}
					} else {
						return {
							name: k,
							value: outcome.data[k],
							type: 'unknown'
						}
					}
				})
			}
		},
		nodeChanged() {
			return this.getNode().data.history?.nodeCompletedEvent
		}
	},
	watch: {
		'$route.query.node'(newVal) {
			if (!newVal) {
				this.close()
			} else if (newVal === this.getNode().id) {
				this.editNode(newVal)
			}
		}
	},
	async created() {
		this.outputs.forEach((o) => {
			o.visible = o.defaultVisible || false
		})
		this.showAllConnectedNodes()
		this.checkUrlForNodeConfig()

		this.originalData = JSON.stringify(this.getNode().data.config)
	},
	beforeUnmount() {
		this.refreshInterval && clearInterval(this.refreshInterval)
	},
	methods: {
		async getVarsMenu() {
			this.flows = (await flowsApi.getAll()).data.results.sort((a, b) => a.name.localeCompare(b.name))

			return {
				title: 'Variables',
				items: [
					{
						label: 'System',
						submenu: {
							title: 'System',
							items: [
								{
									label: 'Current Time',
									dataTransfer: '{{system.current_time(dd/MM/yyyy HH:mm:ss)}}',
									allowsModifier: true
								}
							]
						}
					},
					{
						label: 'Flow Nodes',
						submenu: {
							title: 'Flow Nodes',
							items: this.getCurrentNodes().map((node) => {
								if (!node.data.config) {
									console.log('Node has no config', node)
								}

								const id = node.data.config?.node_id || 'entrypoint'

								return {
									label: id,
									submenu: {
										title: id,
										items: this.getMenuItemsForNode(node).map((o) => {
											if (node.type === 'subflow') {
												return o
											}
											return {
												label: o.label,
												dataTransfer: `{{${id}.${o.value}}}`
											}
										})
									}
								}
							})
						}
					}
				]
			}
		},
		getMenuItemsForNode(node) {
			if (node.type === 'subflow') {
				try {
					const flow = this.flows.find((f) => f.id === node.data.config.flowId)

					if (flow) {
						return (
							flow.data.nodes.map((n) => {
								const id = n.config?.node_id || 'entrypoint'
								return {
									label: id,
									submenu: {
										title: id,
										items:
											this.flowNodes
												.find((n1) => n1.type === n.type)
												?.variables?.map((o) => {
													if (!o) return []
													return {
														label: o.label,
														dataTransfer: `{{${node.data.config.flowId}_${id}.${o.name}}}`
													}
												}) || []
									}
								}
							}) || []
						)
					} else console.log('Flow not found', node.data.config.flowId, this.flows)
				} catch (e) {
					console.log(e)
				}

				return []
			}

			return (
				this.flowNodes
					.find((n) => n.type === node.type)
					?.variables?.map((o) => {
						if (!o) return []
						return {
							value: o.name,
							label: o.label
						}
					}) || []
			)
		},
		saveNodeData(settings) {
			if (
				!settings?.node_id ||
				this.getToolkit()
					.getNodes()
					.find((n) => n.id !== this.getNode().id && n.data?.config?.node_id === settings.node_id)
			) {
				this.$swal({
					title: 'Invalid Reference',
					text: 'Please provide a unique reference within this Flow',
					icon: 'error'
				})
				return
			}

			//eslint-disable-next-line
			if (!/^[a-zA-Z][a-zA-Z0-9_\-]*$/.test(settings.node_id)) {
				this.$swal({
					title: 'Invalid Reference',
					text: 'Invalid reference format, there must be no whitespace and must start with a letter',
					icon: 'error'
				})
				return
			}

			this.getToolkit().updateNode(this.getNode().id, {
				config: settings
			})
			this.onCloseConfirmed()
			this.refreshKey++
		},
		getConnections() {
			return this.getNode().getAllSourceEdges()
		},
		getCurrentNodes() {
			return this.getToolkit().getNodes()
		},
		showAllConnectedNodes(allowHide = false) {
			const connections = this.getConnections()
			connections.forEach((c) => {
				const optionId = c.source.id.split('-').reverse()[0]
				let node = this.outputs.find((o) => o.id == optionId)
				if (node) node.visible = true
			})
			if (allowHide) {
				this.outputs.forEach((o) => {
					if (!connections.find((c) => c.source.id.split('-').reverse()[0] === o.id)) o.visible = false
				})
			}
		},
		registerSettingsCloseHandler(handler) {
			this.settingsCloseHandler = handler
		},
		toggleShowAll() {
			if (this.showAll) this.showAllConnectedNodes(true)
			this.showAll = !this.showAll
		},
		deleteNode() {
			this.getToolkit().removeNode(this.getNode())
		},
		async editNode() {
			this.varsMenu = await this.getVarsMenu()

			const nodeId = this.getNode().id
			if (!this.readonly)
				this.$router.push({ path: this.$route.path, query: { ...this.$route.query, node: nodeId } })
			this.open = true
		},
		async checkUrlForNodeConfig() {
			if (this.readonly) return
			const nodeId = this.$route.query.node
			if (nodeId && nodeId === this.getNode().id) {
				this.varsMenu = await this.getVarsMenu()
				this.open = true
			}
		},
		close() {
			if (this.settingsCloseHandler) {
				const updatedSettings = this.settingsCloseHandler()
				if (updatedSettings) {
					this.$swal({
						title: 'Unsaved Changes',
						text: 'You have unsaved changes. Are you sure you want to close?',
						showCancelButton: true,
						confirmButtonText: 'Clear changes',
						cancelButtonText: 'Cancel',
						showDenyButton: false,
						denyButtonText: 'Cancel'
					}).then((result) => {
						result?.isConfirmed && this.onCloseConfirmed()
					})
				} else {
					this.onCloseConfirmed()
				}
			} else {
				this.onCloseConfirmed()
			}

			return false
		},
		onCloseConfirmed() {
			this.settingsCloseHandler = null
			this.open = false
			this.$router.push({ path: this.$route.path, query: { ...this.$route.query, node: null } })
		},
		getAllNodes() {
			return this.getToolkit()
				.getNodes()
				.map((n) => n.data?.config?.node_id)
				.filter(Boolean)
		}
	}
})
</script>

<style scoped>
@keyframes grow-shadow {
	0% {
		box-shadow: 0 0 2px rgba(205, 242, 126, 0.1);
	}
	20%,
	50%,
	80% {
		box-shadow: 0 0 22px rgba(205, 242, 126, 1);
	}
	100% {
		box-shadow: 0 0 2px rgba(205, 242, 126, 0.1);
	}
}

.pulse {
	animation: grow-shadow 5s ease-in infinite;
}

@keyframes grow-shadow-red {
	0% {
		box-shadow: 0 0 2px rgba(221, 32, 3, 0.1);
	}
	20%,
	50%,
	80% {
		box-shadow: 0 0 22px rgba(221, 32, 3, 1);
	}
	100% {
		box-shadow: 0 0 2px rgba(221, 32, 3, 0.1);
	}
}

.pulseRed {
	animation: grow-shadow-red 5s ease-in infinite;
}
</style>
